/**
 * tooltips.ts
 *
 * Single source of truth for all tooltip copy strings used across the app.
 * Centralising here makes content updates easy and keeps components clean.
 *
 * Naming convention: TOOLTIP_<COMPONENT/SECTION>_<ITEM>
 */

// ── Available Labor card (RosteredHeadcountDisplay) ───────────────────────────

/** Info icon on the "Available Labor" card header */
export const TOOLTIP_AVAILABLE_LABOR_CARD =
  "Displays net available labor hours and headcount, reflecting adjustments from VTO, VET, AZ Ready, Labor Share, and Misc Hours";

/** "Labor Share" column label in the Available Labor table */
export const TOOLTIP_LABOR_SHARE =
  "This represent net ALPS hours planned to move from CRET/AR towards other Depts";

/** "Misc Hours" column label in the Available Labor table */
export const TOOLTIP_MISC_HOURS =
  "These are hours coded to admin functions that need to be excluded for shift planning purposes (eg. AMCARE)";

/**
 * Maps the display label of a roster adjustment category to its tooltip text.
 * Only categories that need a tooltip are included; consumed by RosteredHeadcountDisplay
 * and the Labor Adjustments section in rosterAndRates/index.tsx.
 */
export const CATEGORY_TOOLTIP_MAP: Record<string, string> = {
  "Labor Share":         TOOLTIP_LABOR_SHARE,
  "Miscellaneous Hours": TOOLTIP_MISC_HOURS,
};

// ── Volumes and WIP ───────────────────────────────────────────────────────────

/** "Expected Volume" label in the Process Path Volume Row */
export const TOOLTIP_EXPECTED_VOLUME =
  "Expected Volume is calculated as (ALPS Target * Historical Avg Volume by path)";

// ── TopMetricsRow / Shift Review ──────────────────────────────────────────────

/** "RCC Recommended Primary Grading Volume" metric card */
export const TOOLTIP_RCC_RECOMMENDED_VOLUME =
  "Based on Expected Hours + Overridden Hours";

/** "ALPS Planned Volume" metric card */
export const TOOLTIP_ALPS_PLANNED_VOLUME =
  "No overrides applied";

/** "Overridden Primary Grading Volume" metric card */
export const TOOLTIP_OVERRIDDEN_VOLUME =
  "Based on overrides in Volume and Buffer";

/** "Labor Hours Delta" metric card */
export const TOOLTIP_LABOR_HOURS_DELTA =
  "Against ALPS Planned Volume";

// V2 published shift plan (UTR-REFLEX-1163) metric-card tooltips. Kept separate
// from the V1 constants above so V1's cards are unaffected.
export const TOOLTIP_V2_RCC_RECOMMENDED_VOLUME =
  "The RCC-recommended volume of primary grading units for this shift based on historical throughput and current demand signals.";

export const TOOLTIP_V2_ALPS_PLANNED_VOLUME =
  "The target volume set by ALPS (Automated Labor Planning System) to meet customer demand for this shift";

export const TOOLTIP_V2_LABOR_HOURS_DELTA =
  "Labor Hours Delta = (Total Planned Available Hours) - (ALPS Required Hours to meet volume target). A deficit means you are short-staffed by that many hours; a surplus means you have that much spare capacity.";
